/**
 * Attaching a file to a record — `docs/quiet-records.md` §4.
 *
 * This was the Files tab's private business: an `<input>`, a drag counter, a
 * per-file try, a describe dialog afterwards. The quiet Overview makes the
 * *whole page* a drop target (§6, "Drop a file anywhere on Overview"), so a
 * second copy of all that would have had to agree with the first about the
 * allow-list, about what a failed file does to the ones that worked, and
 * about whether the describe dialog opens once or five times. It would not
 * have, and the drift would have shown up as one screen losing files that the
 * other kept.
 *
 * So the behaviour moved here with none of it changed, and the two surfaces
 * are now two renderings of one hook. **A page that writes its own drop
 * handler is the defect** (design system §9, item 27).
 *
 * **Upload, then describe.** Asking what a file is before it is anywhere
 * means a dialog per file and a refresh that loses all of them. The type is
 * guessed from the name and the content type; the dialog that opens
 * afterwards is an offer, not a gate.
 *
 * **Every file gets its own try.** The whole batch used to sit in one, so a
 * single rejected file — a `.docx` the allow-list does not take, something
 * over 25 MB — threw out of the loop before the reload, and the files that
 * *had* uploaded were never shown. From the outside that reads as "it
 * accepted them, thought about it, and failed". One file failing is one file
 * failing.
 *
 * **Not only a record's.** A task's Attachments (Aaron, Oct 26) are the same
 * picker, drop and per-file try on something that is not a record: `filesAt`
 * names where a file is linked and the list read back, and `null` — New
 * task's — only uploads and hands the files back for the create to link.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { DragEvent, InputHTMLAttributes } from 'react'
import {
  guessFileType, type FileType, type FileVisibility, type RecordRef, type RecordType,
} from '@rowted/shared'
import { request, upload, type UploadedFile } from '../lib/api.js'
/* Type-only, so the cycle back to the Files tab is erased at build time. */
import type { RecordFile } from './record-files.js'

/** What a chip or the phone's attach sheet can decide before the picker opens. */
export interface UploadOptions {
  /** Overrides the guess — the Files tab's type chips (§8). */
  type?: FileType
  /** Internal unless the phone sheet said otherwise. */
  visibility?: FileVisibility
}

/** Where a file came from when it was not a plain upload: the stop it was scoped on (§11.3). */
export interface UploadSource { kind: 'activity'; id: string }

/** What an `uploader` is told about the file it is sending. */
export interface UploaderContext {
  record: RecordRef
  orgUnitId: string
  type: FileType
  visibility: FileVisibility
  source?: UploadSource
  alsoLink: RecordRef[]
}

/**
 * Sends one file and answers its id (`docs/activity.md` §11.3). `linked`
 * says the uploader has attached it to the record (and `alsoLink`) itself —
 * the scope screens' queue does, so a photo taken with no signal is still
 * attached when it goes up after the app was closed — and the hook then
 * leaves the linking alone.
 */
export type Uploader = (file: File, context: UploaderContext) => Promise<{ id: string; linked: boolean }>

export interface RecordUpload {
  /** Upload these, then open the describe dialog on whatever landed. */
  add: (chosen: FileList | File[], options?: UploadOptions) => Promise<void>
  /**
   * Opens the file picker, with the type already decided when a chip said so.
   *
   * The options are stashed rather than passed to `add`, because what the
   * picker returns comes back through the input's own `change` event — the
   * chip is pressed, then the operating system's dialog, then the files.
   * Nothing survives that but a ref.
   */
  openPicker: (options?: UploadOptions) => void
  /** Spread onto the hidden `<input type="file">` the caller renders. */
  inputProps: InputHTMLAttributes<HTMLInputElement> & { ref: typeof undefined }
  /** Spread onto whatever is the drop target — a tab, or the whole Overview. */
  dropProps: {
    onDragEnter: (event: DragEvent) => void
    onDragOver: (event: DragEvent) => void
    onDragLeave: (event: DragEvent) => void
    onDrop: (event: DragEvent) => void
  }
  /** A drag is over the target. The overlay and the dashed tile read it. */
  over: boolean
  /** Mid-upload. `done of total` while a batch is running. */
  busy: boolean
  progress: { done: number; total: number; name: string } | null
  /** Named refusals, joined — the files that did not land. */
  error: string | null
  setError: (message: string | null) => void
  /** The files the describe dialog is about, or null. */
  describing: RecordFile[] | null
  setDescribing: (files: RecordFile[] | null) => void
}

/**
 * Where the files go: a record's Files by default (`object`, `id`). A task's
 * Attachments are not a record — `filesAt` is the path a file is linked at
 * (`POST`, the same body) and the list read back from (`GET`, `{ items }`),
 * and `null` is New task's, whose files are only uploaded (`onUploaded`) and
 * ride on the create.
 */
export type UploadTarget =
  | { object: RecordType; id: string; filesAt?: undefined }
  | { object?: undefined; id?: undefined; filesAt: string | null }

export function useRecordUpload({
  object, id, filesAt, orgUnitId, canEdit, onChanged, onFiles, onUploaded,
  describe = true, source, alsoLink, uploader,
}: UploadTarget & {
  /** Which branch the uploaded file is filed under. */
  orgUnitId: string
  /** A read-only viewer gets no picker and no drop target. */
  canEdit: boolean
  /** Anything that changes a count — the tab pill, the summary. */
  onChanged?: () => void
  /**
   * The freshly-read list, for a caller that is showing one.
   *
   * The Files tab has its own `files` state and would otherwise re-fetch what
   * this hook has just fetched; Overview has no list and ignores it.
   */
  onFiles?: (files: RecordFile[]) => void
  /*
   * Activity's scope screens (`docs/activity.md` §11.3). All four off by
   * default, so every other caller does exactly what it always did.
   */
  /** False: no describe dialog afterwards. */
  describe?: boolean
  /** Sets the file's `source_kind`/`source_id` — the stop it was scoped on. */
  source?: UploadSource
  /** More records to attach it to — the property, for *Also show on the property's photos*. */
  alsoLink?: RecordRef[]
  /** Sends one file instead of `POST /files` — the scope screens' offline queue (§11.4). A record's only. */
  uploader?: Uploader
  /** What went up, as `upload` answered — for `filesAt: null`, where nothing here links them. */
  onUploaded?: (files: UploadedFile[]) => void
}): RecordUpload {
  const [busy, setBusy] = useState(false)
  const [over, setOver] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState<{ done: number; total: number; name: string } | null>(
    null)
  const [describing, setDescribing] = useState<RecordFile[] | null>(null)
  const input = useRef<HTMLInputElement>(null)

  /*
   * **A depth counter, and it counts `dragenter` — never `dragover`.**
   *
   * `dragenter` and `dragleave` fire for every child the pointer crosses, so
   * a plain boolean flickers off the moment a drag passes over a card inside
   * the target: the Files tab got away with it because its target has few
   * children, and Overview — a column of cards — does not. Counting enters
   * against leaves is the fix.
   *
   * `dragover` is the one that must **not** count, because it fires
   * continuously while the pointer is still: counting it climbs for ever and
   * the matching leaves never bring it back to nought, so the overlay sticks
   * after the drag has gone. It is here only for its `preventDefault`.
   */
  const depth = useRef(0)

  /* A drag left half-finished when the component goes is a page that still
     thinks something is over it the next time it mounts. */
  useEffect(() => () => { depth.current = 0 }, [])

  const add = useCallback(async (chosen: FileList | File[], options: UploadOptions = {}) => {
    const list = Array.from(chosen)
    if (list.length === 0 || !canEdit) return
    setBusy(true)
    setError(null)
    setProgress({ done: 0, total: list.length, name: list[0]?.name ?? '' })

    const landed: string[] = []
    const refused: string[] = []
    const wentUp: UploadedFile[] = []
    const record = object && id ? { type: object, id } : null

    for (const [index, file] of list.entries()) {
      setProgress({ done: index, total: list.length, name: file.name })
      try {
        /* The chip's answer beats the guess; with no chip, the guess. */
        const type = options.type ?? guessFileType(file.name, file.type)
        const visibility = options.visibility ?? 'internal'
        let sent: { id: string; linked: boolean }
        if (uploader && record) {
          sent = await uploader(file, {
            record, orgUnitId, type, visibility, source, alsoLink: alsoLink ?? [],
          })
        } else {
          const made = await upload(file, { kind: 'attachment', orgUnitId })
          wentUp.push(made)
          sent = { id: made.id, linked: false }
        }
        if (!sent.linked) {
          const linkBody = { fileId: sent.id, type, visibility, ...(source ? { source } : {}) }
          for (const at of record
            ? [record, ...(alsoLink ?? [])].map((one) => `/records/${one.type}/${one.id}/files`)
            : filesAt ? [filesAt] : []) {
            await request(at, { method: 'POST', body: linkBody })
          }
        }
        landed.push(sent.id)
      } catch (cause) {
        refused.push(`${file.name} — ${
          cause instanceof Error ? cause.message : 'did not upload'}`)
      }
    }

    setBusy(false)
    setProgress(null)
    if (input.current) input.current.value = ''
    setError(refused.length === 0 ? null : refused.join('; '))

    if (landed.length === 0) return
    onUploaded?.(wentUp)

    const listAt = record ? `/records/${record.type}/${record.id}/files` : filesAt
    const body = listAt
      ? await request<{ items: RecordFile[] }>(listAt).catch(() => null)
      : null
    if (body) onFiles?.(body.items)
    onChanged?.()

    /*
     * One dialog, however many files. It is the same questions for all of
     * them — what they are, who can see them, when they expire — and asking
     * five times is four times too many. Names stay as they came in, because
     * that is the one answer that differs per file.
     */
    const uploaded = (body?.items ?? []).filter((file) => landed.includes(file.id))
    if (describe && uploaded.length > 0) setDescribing(uploaded)
  }, [object, id, filesAt, orgUnitId, canEdit, onChanged, onFiles, onUploaded, describe, source, alsoLink, uploader])

  const chosen = useRef<UploadOptions>({})
  const openPicker = useCallback((options: UploadOptions = {}) => {
    chosen.current = options
    input.current?.click()
  }, [])

  const inputProps = useMemo(() => ({
    ref: input as unknown as undefined,
    type: 'file' as const,
    multiple: true,
    className: 'hidden',
    onChange: (event: { target: HTMLInputElement }) => {
      if (event.target.files) void add(event.target.files, chosen.current)
      chosen.current = {}
    },
  }), [add])

  const dropProps = useMemo(() => ({
    onDragEnter: (event: DragEvent) => {
      if (!canEdit) return
      event.preventDefault()
      depth.current += 1
      setOver(true)
    },
    onDragOver: (event: DragEvent) => {
      /*
       * `preventDefault` on *over*, not only on enter: without it the browser
       * treats the element as a non-target and the drop never fires at all,
       * which is the one way this goes wrong silently.
       */
      if (canEdit) event.preventDefault()
    },
    onDragLeave: () => {
      if (!canEdit) return
      depth.current = Math.max(0, depth.current - 1)
      if (depth.current === 0) setOver(false)
    },
    onDrop: (event: DragEvent) => {
      event.preventDefault()
      depth.current = 0
      setOver(false)
      if (!canEdit) return
      void add(event.dataTransfer.files)
    },
  }), [add, canEdit])

  return {
    add,
    openPicker,
    inputProps,
    dropProps,
    over,
    busy,
    progress,
    error,
    setError,
    describing,
    setDescribing,
  }
}
